import React, { useState, useEffect, useCallback } from 'react'
import {
  Grid,
  Typography,
  Paper,
  Button,
  CircularProgress,
  Snackbar,
  Alert,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  ListItemSecondaryAction,
  Divider,
  Box,
} from '@mui/material'
import DownloadIcon from '@mui/icons-material/Download'
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf'
import TableChartIcon from '@mui/icons-material/TableChart'
import EventIcon from '@mui/icons-material/Event'
import Env from '../../shared/config/envConfig'
import { addCsrfHeaders } from '../../shared/config/csrf'

const Reports: React.FC = () => {
  const [loading, setLoading] = useState<string | null>(null)
  const [snackOpen, setSnackOpen] = useState(false)
  const [snackMsg, setSnackMsg] = useState('')
  const [snackSeverity, setSnackSeverity] = useState<'success' | 'error'>('success')
  const [events, setEvents] = useState<any[]>([])
  const [loadingEvents, setLoadingEvents] = useState(false)

  const loadEvents = useCallback(async () => {
    try {
      setLoadingEvents(true)
      const headers: HeadersInit = new Headers()
      headers.set('Accept', 'application/json')
      addCsrfHeaders(headers)
      const url = `${Env.basePath || ''}/api/admin/events`
      const res = await fetch(url, { method: 'GET', credentials: 'include', headers })
      if (!res.ok) {
        const text = await res.text().catch(() => '')
        throw new Error(`HTTP ${res.status} ${text}`)
      }
      const json = await res.json().catch(() => [])
      const arr = Array.isArray(json) ? json : (json?.items || [])
      setEvents(arr)
    } catch (e) {
      console.error('Events laden fehlgeschlagen', e)
    } finally {
      setLoadingEvents(false)
    }
  }, [])

  useEffect(() => {
    loadEvents()
  }, [loadEvents])

  const downloadFile = async (endpoint: string, filename: string, accept = 'application/pdf') => {
    try {
      setLoading(endpoint)
      const headers: HeadersInit = new Headers()
      headers.set('Accept', accept)
      addCsrfHeaders(headers)

      const res = await fetch(`${Env.basePath}${endpoint}`, {
        method: 'GET',
        credentials: 'include',
        headers,
      })

      if (!res.ok) {
        const text = await res.text().catch(() => '')
        throw new Error(`Download fehlgeschlagen: ${res.status} ${text}`)
      }

      const blob = await res.blob()
      const url = window.URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = url
      a.download = filename
      document.body.appendChild(a)
      a.click()
      window.URL.revokeObjectURL(url)
      document.body.removeChild(a)

      setSnackMsg('Download erfolgreich gestartet.')
      setSnackSeverity('success')
      setSnackOpen(true)
    } catch (err: any) {
      console.error(err)
      setSnackMsg(err.message || 'Fehler beim Herunterladen.')
      setSnackSeverity('error')
      setSnackOpen(true)
    } finally {
      setLoading(null)
    }
  }

  return (
    <Grid container sx={{ mt: 3, mb: 4 }}>
      <Grid item xs={12}>
        <h2>Reports</h2>
      </Grid>
      <Typography variant='body1' sx={{ mb: 3 }}>
        Hier findest Du verschiedene Listen zum Download.
      </Typography>

      <Grid container spacing={3}>
        <Grid item xs={12} md={6}>
          <Paper elevation={2} sx={{ p: 0, borderRadius: 2 }}>
            <List>
              <ListItem>
                <ListItemIcon>
                  <PictureAsPdfIcon color='primary' />
                </ListItemIcon>
                <ListItemText
                  primary='Aktive Mitglieder'
                  secondary='Liste aller derzeit aktiven Mitglieder im MARKENCAMP e.V.'
                />
                <ListItemSecondaryAction>
                  <Button
                    variant='outlined'
                    color='primary'
                    startIcon={loading === '/api/admin/reports/active-members/pdf' ? <CircularProgress size={20} /> : <DownloadIcon />}
                    onClick={() => downloadFile('/api/admin/reports/active-members/pdf', 'aktive_mitglieder.pdf')}
                    disabled={loading !== null}
                  >
                    Download
                  </Button>
                </ListItemSecondaryAction>
              </ListItem>
            </List>
          </Paper>
        </Grid>

        <Grid item xs={12} md={6}>
          <Paper elevation={2} sx={{ p: 0, borderRadius: 2 }}>
            <Typography variant='h6' sx={{ p: 2, pb: 0 }}>
              Teilnehmerlisten
            </Typography>
            <List>
              {loadingEvents && (
                <ListItem>
                  <CircularProgress size={24} sx={{ mr: 2 }} />
                  <ListItemText primary='Events werden geladen...' />
                </ListItem>
              )}
              {!loadingEvents && events.length === 0 && (
                <ListItem>
                  <ListItemText primary='Keine Events gefunden' />
                </ListItem>
              )}
              {events.map((ev, index) => {
                const id = ev.id || ev.uuid
                const name = ev.name || ev.title || ev.label || 'Unbenanntes Event'
                const pdfEndpoint = `/api/admin/reports/event/${id}/participants/pdf`
                const excelEndpoint = `/api/admin/reports/event/${id}/participants/excel`
                const pdfFilename = `Teilnehmerliste_${name.replace(/\s+/g, '_')}.pdf`

                return (
                  <React.Fragment key={id}>
                    {index > 0 && <Divider variant='inset' component='li' />}
                    <ListItem>
                      <ListItemIcon>
                        <EventIcon color='secondary' />
                      </ListItemIcon>
                      <ListItemText
                        primary={name}
                        secondary={ev.startDate ? new Date(ev.startDate).toLocaleDateString() : ev.location}
                      />
                      <ListItemSecondaryAction>
                        <Box sx={{ display: 'flex', gap: 1 }}>
                          <Button
                            variant='outlined'
                            color='secondary'
                            size='small'
                            startIcon={loading === pdfEndpoint ? <CircularProgress size={20} /> : <PictureAsPdfIcon />}
                            onClick={() => downloadFile(pdfEndpoint, pdfFilename, 'application/pdf')}
                            disabled={loading !== null}
                          >
                            PDF
                          </Button>
                          <Button
                            variant='outlined'
                            color='success'
                            size='small'
                            startIcon={loading === excelEndpoint ? <CircularProgress size={20} /> : <TableChartIcon />}
                            onClick={() => downloadFile(excelEndpoint, 'Teilnehmerliste.xlsx', 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet')}
                            disabled={loading !== null}
                          >
                            Excel
                          </Button>
                        </Box>
                      </ListItemSecondaryAction>
                    </ListItem>
                  </React.Fragment>
                )
              })}
            </List>
          </Paper>
        </Grid>
      </Grid>

      <Snackbar open={snackOpen} autoHideDuration={4000} onClose={() => setSnackOpen(false)}>
        <Alert severity={snackSeverity} onClose={() => setSnackOpen(false)} sx={{ width: '100%' }}>
          {snackMsg}
        </Alert>
      </Snackbar>
    </Grid>
  )
}

export default Reports
